import { all, take, put, takeLatest, takeEvery, call, select } from 'redux-saga/effects'
import { SubmissionError, setSubmitSucceeded, setSubmitFailed, submit } from 'redux-form'
import { searchUniversities, searchDegrees, fetchEducation, createEducation, updateEducation, deleteEducation } from '../api'
import { anyRecordsMissingInformation, currentlyEditingFormNames, hasEducationRecords } from '../../selectors/education'
import { errorHandler } from '../../../CandidateExperience/helpers/errorHelpers'
import { getLocation } from '../../selectors/router'
import { nextOnboardingUrl } from '../../helpers/onboardingSteps'

import {
  fetchEducationSuccess,
  fetchEducationError,
  createEducationSuccess,
  createEducationError,
  updateEducationSuccess,
  updateEducationError,
  deleteEducationSuccess,
  deleteEducationError,
  searchUniversitiesSuccess,
  searchUniversitiesError,
  searchDegreesSuccess,
  searchDegreesError,
  FETCH,
  UPDATE,
  CREATE,
  DELETE,
  SEARCH_UNIVERSITIES,
  SEARCH_DEGREES,
  SUBMIT,
} from '../modules/education'
import { updateCandidate } from '../modules/candidate'

const SET_SUBMIT_SUCCEEDED = setSubmitSucceeded().type
const SET_SUBMIT_FAILED = setSubmitFailed().type

function* fetchEducationSaga({ meta }) {
  try {
    const response = yield fetchEducation(meta.candidateId)
    yield put(fetchEducationSuccess(response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(fetchEducationError(error))
  }
}

function* createEducationSaga({ meta }) {
  try {
    const response = yield call(createEducation, meta.candidateId, meta.data)
    yield call(meta.resolve)
    yield put(createEducationSuccess(response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(createEducationError(error))
    yield call(meta.reject, new SubmissionError({ _error: error }))
  }
}

function* updateEducationSaga({ meta }) {
  try {
    const response = yield call(updateEducation, meta.id, meta.data)
    yield call(meta.resolve)
    yield put(updateEducationSuccess(response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(updateEducationError(error))
    yield call(meta.reject, new SubmissionError({ _error: error }))
  }
}

function* deleteEducationSaga({ meta }) {
  try {
    // Fake the deletion, then actually delete
    yield put(deleteEducationSuccess(meta.id))
    yield deleteEducation(meta.id)
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(deleteEducationError(error))
  }
}

function* searchUniversitiesSaga({ meta, payload }) {
  try {
    const response = yield searchUniversities(meta.term)
    yield put(searchUniversitiesSuccess(payload.id, response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(searchUniversitiesError(error))
  }
}

function* searchDegreesSaga({ meta, payload }) {
  try {
    const response = yield searchDegrees(meta.term)
    yield put(searchDegreesSuccess(payload.id, response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(searchDegreesError(error))
  }
}

function* submitEducationForm(formName) {
  // Submit the form
  yield put(submit(formName))

  // Resolve upon either successful submission or failure of the form
  yield take(action => {
    return (action.type === SET_SUBMIT_SUCCEEDED || action.type === SET_SUBMIT_FAILED) && action.meta.form === formName
  })
}

function* submitEducationSaga({ meta, payload: { data } }) {
  const location = yield select(getLocation)
  const url = nextOnboardingUrl(location.pathname)

  try {
    // Submit education forms that are currently being edited
    // Await successful submission or failure of each form
    const formNames = yield select(currentlyEditingFormNames)
    yield all(formNames.map(formName => call(submitEducationForm, formName)))

    const hasRecords = yield select(hasEducationRecords)
    const missingInformation = yield select(anyRecordsMissingInformation)

    if (!hasRecords && !data.no_education) {
      yield call(
        meta.reject,
        new SubmissionError({
          _error: 'Please add at least one education record before continuing.',
        }),
      )
    } else if (missingInformation) {
      yield call(
        meta.reject,
        new SubmissionError({
          _error: 'Please complete the missing information in your education records before continuing.',
        }),
      )
    } else if (hasRecords && data.no_education) {
      // Override indication of no formal education if provided
      yield put(updateCandidate({ no_education: false, onboarding_phase: 'wrap_up' }, url))
    } else {
      yield put(updateCandidate({ no_education: data.no_education, onboarding_phase: 'wrap_up' }, url))
    }
  } catch (err) {
    yield call(errorHandler, err)
  }
}

export default function* watchEducationSaga() {
  yield takeLatest(FETCH, fetchEducationSaga)
  yield takeEvery(UPDATE, updateEducationSaga)
  yield takeEvery(CREATE, createEducationSaga)
  yield takeLatest(DELETE, deleteEducationSaga)
  yield takeLatest(SEARCH_UNIVERSITIES, searchUniversitiesSaga)
  yield takeLatest(SEARCH_DEGREES, searchDegreesSaga)
  yield takeLatest(SUBMIT, submitEducationSaga)
}
